<template>
  <section ref="root" class="flex flex-col gap-3 sm:rounded-3xl sm:border sm:border-edge sm:bg-panel sm:p-6">
    <div class="flex items-baseline justify-between gap-2">
      <h2 class="font-display text-[22px] font-extrabold sm:text-[26px]">Strafen pro Mitglied</h2>
      <span class="text-xs text-slate-400 sm:text-sm">{{ hint }}</span>
    </div>

    <p v-if="rows.length === 0" class="py-6 text-center text-slate-400">Keine Strafen in dieser Saison.</p>

    <ul v-else class="flex flex-col gap-1">
      <li
        v-for="row in rows"
        :key="row.name"
        class="relative"
        :class="{ 'z-20': active === row.name }"
        @mouseenter="hoverOpen(row.name)"
        @mouseleave="hoverClose(row.name)"
      >
        <button
          type="button"
          @click="toggle(row.name)"
          :aria-expanded="active === row.name"
          :aria-controls="`fine-details-${row.index}`"
          class="grid w-full grid-cols-[72px_minmax(0,1fr)_56px] items-center gap-2.5 rounded-lg py-1 text-left transition-colors hover:bg-edge/50 sm:grid-cols-[88px_minmax(0,1fr)_64px] sm:gap-3 sm:px-1"
          :class="{ 'bg-edge/50': active === row.name }"
        >
          <span class="truncate text-sm font-medium sm:text-[15px]">{{ row.name }}</span>
          <div class="h-3 overflow-hidden rounded-md bg-edge sm:h-3.5">
            <div
              class="h-full rounded-md motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out"
              :style="{ width: grown ? row.width : '0%', backgroundColor: row.color }"
            ></div>
          </div>
          <span class="text-right text-sm font-semibold tabular-nums sm:text-[15px]">{{ formatEuro(row.value) }}</span>
        </button>

        <!-- Aufteilung nach Kategorie -->
        <transition name="pop">
          <div
            v-if="active === row.name"
            :id="`fine-details-${row.index}`"
            role="region"
            :aria-label="`Strafen von ${row.name} nach Kategorie`"
            class="absolute inset-x-0 top-full mt-1.5 flex flex-col gap-3 rounded-2xl border border-edge bg-night p-3.5 shadow-2xl shadow-black/50 sm:left-[88px] sm:right-[64px]"
          >
            <div class="flex items-baseline justify-between gap-2">
              <span class="text-sm font-semibold">{{ row.name }}</span>
              <span class="text-xs text-slate-400">{{ row.count }} {{ row.count === 1 ? 'Strafe' : 'Strafen' }}</span>
            </div>
            <div class="flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-edge" aria-hidden="true">
              <div
                v-for="cat in row.categories"
                :key="cat.type"
                :style="{ width: cat.width, backgroundColor: cat.color }"
              ></div>
            </div>
            <ul class="flex flex-col gap-1.5">
              <li v-for="cat in row.categories" :key="cat.type" class="flex items-center gap-2 text-sm">
                <span class="h-2.5 w-2.5 flex-none rounded-[3px]" :style="{ backgroundColor: cat.color }"></span>
                <span class="min-w-0 flex-1 truncate">{{ cat.type }}</span>
                <span class="text-xs tabular-nums text-slate-400">{{ cat.count }}×</span>
                <span class="w-14 text-right font-semibold tabular-nums">{{ formatEuro(cat.value) }}</span>
              </li>
            </ul>
          </div>
        </transition>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Punishment from '@/types/Punischment';
import { FineCategory } from '@/services/SeasonService';
import { formatEuro, percentOf } from '@/utils/format';
import { useGrow } from '@/composables/useGrow';
import { useDetailPopover } from '@/composables/useDetailPopover';

const props = defineProps<{
  fines: Punishment[];
  details: Record<string, FineCategory[]>;
  categoryColors: Record<string, string>;
}>();

const rows = computed(() => {
  const sorted = [...props.fines].sort((a, b) => b.value - a.value);
  const max = sorted[0]?.value ?? 0;
  return sorted.map((f, index) => {
    const categories = props.details[f.name] ?? [];
    const total = categories.reduce((sum, c) => sum + c.value, 0);
    return {
      ...f,
      index,
      width: percentOf(f.value, max),
      count: categories.reduce((sum, c) => sum + c.count, 0),
      categories: categories.map((c) => ({
        ...c,
        color: props.categoryColors[c.type] ?? '#94A3B8',
        width: total > 0 ? `${(c.value / total) * 100}%` : '0%',
      })),
    };
  });
});

const { active, root, hint, hoverOpen, hoverClose, toggle } = useDetailPopover(() => props.fines);

const grown = useGrow(() => props.fines);
</script>
